import { Trans } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { reconcileConsoleCreditCheckout } from "../api/client";
import { consoleQueryKeys } from "../api/queries";
import { checkoutOrderId, legacyTopupBalanceIncreased } from "../lib/getStarted";
import { ConButton } from "./atoms";

const LEGACY_CHECKOUT_BASELINE_KEY = "ando.engine.topup.balance-before";

function clearReturnParameters(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("topup");
  url.searchParams.delete("order_id");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

/** One return flow for all Console top-up entrypoints. A redirect is never payment proof. */
export function TopupReturnStatus({ balanceCents }: { balanceCents?: number }): React.ReactElement | null {
  const queryClient = useQueryClient();
  const [state, setState] = React.useState<"idle" | "verifying" | "pending" | "paid" | "legacy" | "legacy-updated">("idle");
  const [retry, setRetry] = React.useState(0);

  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).get("topup") !== "success") return;
    const orderId = checkoutOrderId(window.location.search);
    if (!orderId) {
      setState("legacy");
      return;
    }
    setState("verifying");
    let cancelled = false;
    let attempts = 0;
    let timer: number | undefined;
    const verify = async (): Promise<void> => {
      attempts += 1;
      try {
        const result = await reconcileConsoleCreditCheckout(orderId);
        if (cancelled) return;
        if (result.status === "paid") {
          await queryClient.invalidateQueries({ queryKey: consoleQueryKeys.credits() });
          if (cancelled) return;
          setState("paid");
          clearReturnParameters();
          return;
        }
      } catch {
        // A restart or network failure leaves the payment state unknown.
      }
      if (cancelled) return;
      if (attempts >= 8) setState("pending");
      else timer = window.setTimeout(() => void verify(), 1500);
    };
    void verify();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [queryClient, retry]);

  React.useEffect(() => {
    if (state !== "legacy" || !legacyTopupBalanceIncreased(
      window.sessionStorage.getItem(LEGACY_CHECKOUT_BASELINE_KEY),
      balanceCents,
    )) return;
    setState("legacy-updated");
    window.sessionStorage.removeItem(LEGACY_CHECKOUT_BASELINE_KEY);
    clearReturnParameters();
  }, [balanceCents, state]);

  if (state === "idle") return null;
  if (state === "verifying") return (
    <p className="flex items-center gap-2 text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-sm)]">
      <Loader2 size={15} className="animate-spin" />
      <Trans>Sto verificando il pagamento e aggiornando il saldo…</Trans>
    </p>
  );
  if (state === "paid") return (
    <p className="text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-sm)]">
      <Trans>Pagamento verificato. Il saldo è stato aggiornato.</Trans>
    </p>
  );
  if (state === "legacy-updated") return (
    <p className="text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-sm)]">
      <Trans>Il saldo è stato aggiornato.</Trans>
    </p>
  );
  return (
    <div className="flex flex-wrap items-center gap-3 text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-sm)]">
      {state === "legacy" ? (
        <Trans>Sto aspettando l’aggiornamento del saldo. Controlla la transazione in Stripe prima di ripetere la ricarica.</Trans>
      ) : (
        <Trans>Non riesco ancora a verificare il pagamento. Non ripetere la ricarica; riprova la verifica.</Trans>
      )}
      <ConButton
        variant="outline"
        onClick={() => {
          if (state === "legacy") void queryClient.invalidateQueries({ queryKey: consoleQueryKeys.credits() });
          else setRetry((value) => value + 1);
        }}
      >
        {state === "legacy" ? <Trans>Aggiorna saldo</Trans> : <Trans>Verifica pagamento</Trans>}
      </ConButton>
    </div>
  );
}
